import React from 'react'
import { Flags } from 'react-feature-flags'

export const requirements = {
    covered_by_a_specific_plan_2_6: {
        label: 'Specific Plan',
        description: (
            <>
                <p>
                    The project is undertaken to implement and is consistent with a specific plan for which an
                    environmental impact report has been certified.
                </p>
                <p>
                    <em>
                        Site Check's data only considers whether the parcel may be covered by a specific plan. Please
                        confirm with the local agency that the specific plan was analyzed with an environmental impact
                        report. Note, some areas may be covered by specific plans that have not been inputted into this
                        tool, which is why this requirement may have a ? symbol.
                    </em>
                </p>
            </>
        )
    },
    earthquake_hazard_zone_9_2: {
        label: 'Earthquake ',
        description: (
            <p>
                The site cannot be subject to seismic risk as a result of being within a delineated earthquake fault
                zone, as determined pursuant to Section 2622, or a seismic hazard zone, as determined pursuant to
                Section 2696,{' '}
                <em>
                    unless the applicable general plan or zoning ordinance contains provisions to mitigate the risk of
                    an earthquake fault or seismic hazard zone.
                </em>
            </p>
        )
    },
    landslide_hazard_9_5: {
        label: 'Landslide, Floodplain, Floodway, or Restriction Zone',
        description: (
            <p>
                The transit priority project site is not subject to landslide hazard, flood plain, flood way, or
                restriction zone, unless the applicable general plan or zoning ordinance contains provisions to mitigate
                the risk of a landslide or flood.
            </p>
        )
    },
    low_vehicle_travel_area_3_8: {
        label: 'Low Vehicle Travel Area',
        description: (
            <p>
                "Low vehicle travel area" means a traffic analysis zone that exhibits a below average existing level of
                travel as determined using a regional travel demand model. For residential projects, travel refers to
                either home-based or household vehicle miles traveled per capita. For commercial and retail projects,
                travel refers to non-work attraction trip length; however, where such data are not available, commercial
                projects reference either home-based or household vehicle miles traveled per capita.
            </p>
        )
    },
    low_vmt_15_percent_below_region: {
        label: 'Low Vehicle Travel Area',
        description: (
            <p>
                An area that is surrounded by or adjacent to existing residential development that generates vehicle
                travel per capita under 85 percent of either regional vehicle miles traveled per capita or city vehicle
                miles traveled per capita.
            </p>
        )
    },
    riparian_areas_8_2: {
        label: 'Riparian Areas',
        description: (
            <>
                <p>The site cannot contain riparian areas.</p>
                <p>
                    "Riparian areas" means those areas transitional between terrestrial and aquatic ecosystems and that
                    are distinguished by gradients in biophysical conditions, ecological processes, and biota. A
                    riparian area is an area through which surface and subsurface hydrology connect waterbodies with
                    their adjacent uplands. A riparian area includes those portions of terrestrial ecosystems that
                    significantly influence exchanges of energy and matter with aquatic ecosystems. A riparian area is
                    adjacent to perennial, intermittent, and ephemeral streams, lakes, and estuarine-marine shorelines.
                </p>
            </>
        )
    },
    special_habitats_8_3: {
        label: 'Habitat',
        description: (
            <>
                <p>The site cannot have significant value as a wildlife habitat.</p>
                <p>
                    "Wildlife habitat" means the ecological communities upon which wild animals, birds, plants, fish,
                    amphibians, and invertebrates depend for their conservation and protection.
                </p>
                <p>
                    Habitat of "significant value" includes wildlife habitat of national, statewide, regional, or local
                    importance; habitat for species protected by the federal Endangered Species Act of 1973 (16 U.S.C.
                    Sec. 1531, et seq.), the California Endangered Species Act (Chapter 1.5 (commencing with Section
                    2050) of Division 3 of the Fish and Game Code), or the Native Plant Protection Act (Chapter 10
                    (commencing with Section 1900) of Division 2 of the Fish and Game Code); habitat identified as
                    candidate, fully protected, sensitive, or species of special status by local, state, or federal
                    agencies; or habitat essential to the movement of resident or migratory wildlife.
                </p>
            </>
        )
    },
    state_conservancy_9_6: {
        label: 'State Conservancy',
        description: <p>The site cannot be located within the boundaries of a state conservancy.</p>
    },
    transit_priority_area_3_3: {
        label: 'Transit Proximity',
        description: (
            <>
                <p>
                    The project is proposed within a transit priority area, as defined in subdivision (a) of PRC §
                    21099:
                </p>
                <p>
                    "Transit priority area" means an area within one-half mile of a major transit stop that is existing
                    or planned, if the planned stop is scheduled to be completed within the planning horizon included in
                    a Transportation Improvement Program or applicable regional transportation plan.
                </p>
            </>
        )
    },
    unincorporated_2_4: {
        label: 'Unincorporated Area',
        description: <p>The proposed development occurs within an unincorporated area of a county.</p>
    },
    urban_area_prc_21094_2_2: {
        label: 'Urban Area',
        description: (
            <>
                <p>Within an Urban Area</p>
                <p>
                    "Urban area" includes either an incorporated city or an unincorporated area that is completely
                    surrounded by one or more incorporated cities that meets both of the following criteria:
                </p>
                <ol type="A">
                    <li>
                        The population of the unincorporated area and the population of the surrounding incorporated
                        cities equal a population of 100,000 or more.
                    </li>
                    <li>
                        The population density of the unincorporated area is equal to, or greater than, the population
                        density of the surrounding cities.
                    </li>
                </ol>
            </>
        )
    },
    urbanized_area_or_urban_cluster: {
        label: 'Urbanized Area',
        description: (
            <p>
                The project is located on a site that is a legal parcel or parcels wholly within the boundaries of an
                urbanized area or urban cluster, as designated by the United States Census Bureau.
            </p>
        )
    },
    urbanized_area_prc_21071_2_1: {
        label: 'Urbanized Area',
        description: (
            <>
                <p>Must be located within an "Urbanized area" as defined by PRC § 21071.</p>
                <p>"Urbanized area" means either of the following:</p>
                <ol type="a">
                    <li>
                        An incorporated city that meets either of the following criteria:
                        <ol type="1" className="is-indented">
                            <li>Has a population of at least 100,000 persons.</li>
                            <li>
                                Has a population of less than 100,000 persons if the population of that city and not
                                more than two contiguous incorporated cities combined equals at least 100,000 persons.
                            </li>
                        </ol>
                    </li>
                    <li>
                        An unincorporated area that satisfies the criteria in both paragraph (1) and (2) of the
                        following criteria:
                        <ol type="1" className="is-indented">
                            <li>
                                Is either of the following:
                                <ol type="A" className="is-indented">
                                    <li>
                                        Completely surrounded by one or more incorporated cities, and both of the
                                        following criteria are met:
                                        <ol type="i" className="is-indented">
                                            <li>
                                                The population of the unincorporated area and the population of the
                                                surrounding incorporated city or cities equals not less than 100,000
                                                persons.
                                            </li>
                                            <li>
                                                The population density of the unincorporated area at least equals the
                                                population density of the surrounding city or cities.
                                            </li>
                                        </ol>
                                    </li>
                                    <li>
                                        Located within an urban growth boundary and has an existing residential
                                        population of at least 5,000 persons per square mile. For purposes of this
                                        subparagraph, an "urban growth boundary" means a provision of a locally adopted
                                        general plan that allows urban uses on one side of the boundary and prohibits
                                        urban uses on the other side.
                                    </li>
                                </ol>
                            </li>
                            <li>
                                The board of supervisors with jurisdiction over the unincorporated area has previously
                                taken both of the following actions:
                                <ol type="A" className="is-indented">
                                    <li>
                                        Issued a finding that the general plan, zoning ordinance, and related policies
                                        and programs applicable to the unincorporated area are consistent with
                                        principles that encourage compact development in a manner that does both of the
                                        following:
                                        <ol type="i" className="is-indented">
                                            <li>
                                                Promotes efficient transportation systems, economic growth, affordable
                                                housing, energy efficiency, and an appropriate balance of jobs and
                                                housing.
                                            </li>
                                            <li>Protects the environment, open space, and agricultural areas.</li>
                                        </ol>
                                    </li>
                                    <li>
                                        Submitted a draft finding to the Office of Land Use and Climate Innovation at least 30
                                        days prior to issuing a final finding, and allowed the office 30 days to submit
                                        comments on the draft findings to the board of supervisors.
                                    </li>
                                </ol>
                            </li>
                        </ol>
                    </li>
                    <p>
                        Note:{' '}
                        <em>
                            Site Check has determined that the parcel meets the criteria in subdivision (a) or
                            (b)(1)(A). The criteria in subdivision (b)(1)(B) cannot be mapped at this time. Confirm that
                            the requirements in subdivision (b)(2) have been met.
                        </em>
                    </p>
                </ol>
            </>
        )
    },
    wetlands_8_1: {
        label: 'Wetlands',
        description: (
            <>
                <p>The site cannot contain wetlands.</p>
                <p>
                    "Wetlands" has the same meaning as in the United States Fish and Wildlife Service Manual, Part 660
                    FW 2 (June 21, 1993).
                </p>
            </>
        )
    },
    wildfire_hazard_9_3: {
        label: 'Wildfire hazard',
        description: (
            <p>
                The site cannot be subject to a wildland fire hazard, as determined by the Department of Forestry and
                Fire Protection,{' '}
                <em>
                    unless the applicable general plan or zoning ordinance contains provisions to mitigate the risk of a
                    wildland fire hazard.
                </em>
            </p>
        )
    },
    within_city_limits_2_3: {
        label: 'City Limits',
        description: <p>Within city limits</p>
    },
    within_half_mile_major_transit_: {
        label: 'Major Transit Stop',
        description: (
            <p>
                Within ½ mile of a major transit stop. "Major Transit Stop" means a site containing an existing rail
                transit station, a ferry terminal served by either a bus or rail transit service, or the intersection of
                two or more major bus routes with frequencies of service intervals of 15 minutes or less during the
                morning and afternoon peak commute periods. For the purposes of this exemption, an "existing major
                transit stop" may include a planned and funded stop that is included in an adopted regional
                transportation improvement program.
            </p>
        )
    },
    within_half_mile_transit_corrid: {
        label: 'High-Quality Transit Corridor',
        description: (
            <p>
                Within ½ mile of a high quality transit corridor. "High-quality transit corridor" means an existing
                corridor with fixed route bus service with service intervals no longer than 15 minutes during peak
                commute hours. For the purposes of this exemption, an "existing stop along a high-quality transit
                corridor" may include a planned and funded stop that is included in an adopted regional transportation
                improvement program.
            </p>
        )
    },
    within_mpo_2_5: {
        label: 'Within an MPO',
        description: <p>Must be located within the boundaries of a Metropolitan Planning Organization.</p>
    }
}

export const exemptionCategories = [
    {
        category: 'full',
        label: 'Statutory Exemptions',
        description: (
            <p>
                Statutory exemptions are types of projects that the California Legislature has decided are not subject
                to CEQA. A statutory exemption applies to any given project that falls under its definition, regardless
                of the project’s potential impacts to the environment. If a project falls under the definition, then no
                further action under CEQA is required (unless special noticing provisions apply, as highlighted below).
            </p>
        ),
        exemptions: [
            {
                code: 'e_21155_1',
                label: 'PRC § 21155.1 (SB 375, Transit Priority Projects)',
                notes: (
                    <p>
                        This exemption is for transit priority projects, which are defined in PRC § 21155. If a project
                        qualifies as a transit priority project but does not meet all the requirements in PRC § 21155.1,
                        the project may still qualify for streamlining under PRC § 21155.2.
                    </p>
                ),
                requirements: {
                    spatial: [
                        'within_mpo_2_5',
                        {
                            label: 'Transit proximity',
                            description: <p>Any of the following:</p>,
                            op: 'or',
                            show: true,
                            requirements: [
                                {
                                    label: 'Rail Transit',
                                    description: (
                                        <p>
                                            Within ½ mile of a rail transit station or a ferry terminal included in a
                                            regional transportation plan.
                                        </p>
                                    ),
                                    requirements: ['within_half_mile_rail_transit_s']
                                },
                                {
                                    label: 'High-Quality Transit Corridor',
                                    description: (
                                        <>
                                            <p>Within ¼ mile of an existing high-quality transit corridor.</p>
                                            <p>
                                                A high-quality transit corridor means a corridor with fixed route bus
                                                service with service intervals no longer than 15 minutes during peak
                                                commute hours.
                                            </p>
                                        </>
                                    ),
                                    requirements: ['within_quarter_mile_transit_cor']
                                },
                                {
                                    label: 'Planned High-Quality Transit Corridor',
                                    description: (
                                        <p>
                                            Within ¼ mile of a planned high-quality transit corridor included in a
                                            regional transportation plan.
                                        </p>
                                    ),
                                    requirements: ['planned_rtp_quarter_mile_hqtc_3']
                                }
                            ]
                        },
                        'wetlands_8_1',
                        'riparian_areas_8_2',
                        'special_habitats_8_3',
                        'earthquake_hazard_zone_9_2',
                        {
                            label: 'Landslide',
                            description: (
                                <>
                                    <p>
                                        The site cannot be subject to a landslide hazard,{' '}
                                        <em>
                                            unless the applicable general plan or zoning ordinance contains provisions
                                            to mitigate the risk of a landslide.
                                        </em>
                                    </p>
                                    <p>
                                        Site Check's data only considers areas that were evaluated by the DOC. Please
                                        confirm with the local agency if the project is located within an area that may
                                        have known hazards.
                                    </p>
                                </>
                            ),
                            requirements: ['landslide_hazard_9_5']
                        },
                        {
                            label: 'Floodplain, Floodway, or Restriction Zone',
                            description: (
                                <p>
                                    The site cannot be in a flood plain, flood way, or restriction zone,{' '}
                                    <em>
                                        unless the applicable general plan or zoning ordinance contains provisions to
                                        mitigate the risk of a flood.
                                    </em>
                                </p>
                            ),
                            requirements: ['flood_plain_9_4']
                        },
                        'wildfire_hazard_9_3'
                    ],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Residential or Mixed-Use (at least 50 percent residential)</p>
                        },
                        {
                            label: 'SCS Consistency',
                            description: (
                                <p>
                                    Must be consistent with the general use designation, density, building intensity,
                                    and applicable policies specified for the project area in the applicable SCS.
                                </p>
                            )
                        },
                        {
                            label: 'Acreage Limitation',
                            description: <p>The site is not more than eight acres in total area.</p>
                        },
                        {
                            label: 'Number of Units',
                            description: <p>200 residential units or less.</p>
                        },
                        {
                            label: 'Size Limitations',
                            description: (
                                <p>
                                    The project does not include any single level building that exceeds 75,000 square
                                    feet.
                                </p>
                            )
                        },
                        {
                            label: 'Density',
                            description: (
                                <p>
                                    Must provide a minimum net density of at least 20 dwelling units per acre. If the
                                    project contains between 26 percent and 50 percent nonresidential uses, it must have
                                    a floor area ratio of not less than 0.75.
                                </p>
                            )
                        },
                        {
                            label: 'Surrounding Industrial Uses',
                            description: (
                                <p>The project is determined not to conflict with nearby operating industrial uses.</p>
                            )
                        },
                        {
                            label: 'Affordable Housing',
                            description: (
                                <p>
                                    The project does not result in any net loss in the number of affordable housing
                                    units within the project area.
                                </p>
                            )
                        },
                        {
                            label: 'Affordable Housing/Open Space',
                            description: (
                                <>
                                    <p>Must meet one of the following:</p>
                                    <ol type="1">
                                        <li>
                                            At least 20 percent of the housing will be sold to families of moderate
                                            income, or not less than 10 percent of the housing will be rented to
                                            families of low income, or not less than 5 percent of the housing is rented
                                            to families of very low income. The transit priority project developer
                                            provides sufficient legal commitments to the appropriate local agency to
                                            ensure the continued availability and use of the housing units for very low,
                                            low-, and moderate-income households at monthly housing costs with an
                                            affordable housing cost or affordable rent, as defined in HSC § 50052.5 or
                                            50053, respectively, for the period required by the applicable financing.
                                            Rental units shall be affordable for at least 55 years. Ownership units
                                            shall be subject to resale restrictions or equity sharing requirements for
                                            at least 30 years.
                                        </li>
                                        <li>
                                            The transit priority project developer has paid or will pay in-lieu fees
                                            pursuant to a local ordinance in an amount sufficient to result in the
                                            development of an equivalent number of units that would otherwise be
                                            required pursuant to paragraph (1).
                                        </li>
                                        <li>
                                            The transit priority project provides public open space equal to or greater
                                            than five acres per 1,000 residents of the project.
                                        </li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Open Space',
                            description: (
                                <>
                                    <p>The project site is not located on developed open space.</p>
                                    <ol type="A">
                                        <li>
                                            For the purposes of this paragraph, "developed open space" means land that
                                            meets all of the following criteria:
                                            <ol type="i">
                                                <li>
                                                    Is publicly owned, or financed in whole or in part by public funds.
                                                </li>
                                                <li>Is generally open to, and available for use by, the public.</li>
                                                <li>
                                                    Is predominantly lacking in structural development other than
                                                    structures associated with open spaces, including, but not limited
                                                    to, playgrounds, swimming pools, ballfields, enclosed child play
                                                    areas, and picnic facilities.
                                                </li>
                                            </ol>
                                        </li>
                                        <li>
                                            For the purposes of this paragraph, "developed open space" includes land
                                            that has been designated for acquisition by a public agency for developed
                                            open space, but does not include lands acquired with public funds dedicated
                                            to the acquisition of land for housing purposes.
                                        </li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Utilities',
                            description: (
                                <p>
                                    The transit priority project and other projects approved prior to the approval of
                                    the transit priority project but not yet built can be adequately served by existing
                                    utilities, and the transit priority project applicant has paid, or has committed to
                                    pay, all applicable in-lieu or development fees.
                                </p>
                            )
                        },
                        {
                            label: 'Energy Efficiency',
                            description: (
                                <p>
                                    The buildings in the project are 15 percent more energy efficient than required by
                                    Chapter 6 of Title 24 of the California Code of Regulations and the buildings and
                                    landscaping are designed to achieve 25 percent less water usage than the average
                                    household use in the region.
                                </p>
                            )
                        },
                        {
                            label: 'Species',
                            description: (
                                <p>
                                    The project does not harm any species protected by the federal Endangered Species
                                    Act of 1973 (16 U.S.C. Sec. 1531 et seq.), the Native Plant Protection Act (Chapter
                                    10 (commencing with Section 1900) of Division 2 of the Fish and Game Code), or the
                                    California Endangered Species Act (Chapter 1.5 (commencing with Section 2050) of
                                    Division 3 of the Fish and Game Code), and the project does not cause the
                                    destruction or removal of any species protected by a local ordinance in effect at
                                    the time the application for the project was deemed complete.
                                </p>
                            )
                        },
                        {
                            label: 'Site Contamination',
                            description: (
                                <p>
                                    The site is not included on any list of facilities and sites compiled pursuant to
                                    GOV § 65962.5.
                                </p>
                            )
                        },
                        {
                            label: 'High Risk of Fire or Explosion ',
                            description: (
                                <p>
                                    The site is not subject to an unusually high risk of fire or explosion from
                                    materials stored or used on nearby properties.
                                </p>
                            )
                        },
                        {
                            label: 'Hazards',
                            description: (
                                <>
                                    <p>
                                        The site is subject to a preliminary endangerment assessment prepared by an
                                        environmental assessor to determine the existence of any release of a hazardous
                                        substance on the site and to determine the potential for exposure of future
                                        occupants to significant health hazards from any nearby property or activity.
                                    </p>
                                    <p>
                                        If a release of a hazardous substance is found to exist on the site, the release
                                        shall be removed or any significant effects of the release shall be mitigated to
                                        a level of insignificance in compliance with state and federal requirements.
                                    </p>
                                    <p>
                                        If a potential for exposure to significant hazards from surrounding properties
                                        or activities is found to exist, the effects of the potential exposure shall be
                                        mitigated to a level of insignificance in compliance with state and federal
                                        requirements.
                                    </p>
                                </>
                            )
                        },
                        {
                            label: 'Public Health',
                            description: (
                                <p>
                                    The site is not subject to risk of a public health exposure at a level that would
                                    exceed the standards established by any state or federal agency.
                                </p>
                            )
                        },
                        {
                            label: 'Prior Environmental Reviews',
                            description: (
                                <p>
                                    Any applicable mitigation measures or performance standards or criteria set forth in
                                    the prior environmental impact reports, and adopted in findings, have been or will
                                    be incorporated into the transit priority project.
                                </p>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_21155_4',
                label: 'PRC § 21155.4 (SB 743, Specific Plan in TPAs)',
                notes: (
                    <p>
                        This code section provides a complete statutory exemption unless the events specified in PRC §
                        21166 have occurred (see PRC § 21155.4(b)). This exemption also applies to employment centers,
                        as defined in PRC § 21099(a)(1). It covers any subdivision or zoning change needed for the
                        project (see PRC § 21155.4(a)). This exemption is also contained in CEQA Guidelines § 15182.
                    </p>
                ),
                requirements: {
                    spatial: [
                        'transit_priority_area_3_3',
                        {
                            id: 'covered_by_a_specific_plan_2_6',
                            strict: false
                        },
                        'within_mpo_2_5'
                    ],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Residential or Mixed-Use (not defined)</p>
                        },
                        {
                            label: 'SCS Consistency',
                            description: (
                                <p>
                                    The project is consistent with the general use designation, density, building
                                    intensity, and applicable policies specified for the project area in the applicable
                                    sustainable communities strategy.
                                </p>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_21159_24',
                label: 'PRC § 21159.24 (SB 1925, Infill Housing)',
                notes: (
                    <p>
                        This exemption must be read in conjunction with the definitions in PRC § 21159.20 and the
                        environmental limitations in PRC § 21159.21. While a Notice of Exemption (NOE) is usually
                        optional, an NOE must be filed if this exemption is used (see PRC § 21152.1, CEQA Guideline §
                        15196). This exemption is also contained in CEQA Guidelines § 15195.
                    </p>
                ),
                requirements: {
                    spatial: [
                        'urbanized_area_prc_21071_2_1',
                        {
                            label: 'Transit proximity',
                            description: (
                                <>
                                    <p>Within ½ mile of a Major Transit Stop as defined in PRC § 21064.3.</p>
                                    <p>"Major transit stop" means a site containing any of the following:</p>
                                    <ul type="a">
                                        <li>An existing rail or bus rapid transit station.</li>
                                        <li>A ferry terminal served by either a bus or rail transit service.</li>
                                        <li>
                                            The intersection of two or more major bus routes with a frequency of service
                                            interval of 15 minutes or less during the morning and afternoon peak commute
                                            periods.
                                        </li>
                                    </ul>
                                    <p>"Bus rapid transit" is defined in PRC § 21060.2.</p>
                                </>
                            ),
                            requirements: ['within_half_mile_major_transit_']
                        },
                        {
                            label: 'Wetlands',
                            description: (
                                <>
                                    <p>The site cannot contain wetlands.</p>
                                    <p>
                                        For the purposes of this subdivision, "wetlands" has the same meaning as in
                                        Section 328.3 of Title 33 of the Code of Federal Regulations.
                                    </p>
                                </>
                            ),
                            requirements: ['wetlands_8_1']
                        },
                        'state_conservancy_9_6',
                        {
                            label: 'Habitat',
                            description: (
                                <>
                                    <p>The site cannot have any value as a wildlife habitat.</p>
                                    <p>
                                        "Wildlife habitat" means the ecological communities upon which wild animals,
                                        birds, plants, fish, amphibians, and invertebrates depend for their conservation
                                        and protection.
                                    </p>
                                </>
                            ),
                            requirements: ['special_habitats_8_3']
                        },
                        'earthquake_hazard_zone_9_2',
                        {
                            label: 'Landslide',
                            description: (
                                <>
                                    <p>
                                        The site cannot be subject to a landslide hazard,{' '}
                                        <em>
                                            unless the applicable general plan or zoning ordinance contains provisions
                                            to mitigate the risk of a landslide.
                                        </em>
                                    </p>
                                    <p>
                                        Site Check's data only considers areas that were evaluated by the DOC. Please
                                        confirm with the local agency if the project is located within an area that may
                                        have known hazards.
                                    </p>
                                </>
                            ),
                            op: 'and',
                            requirements: ['landslide_hazard_9_5']
                        },
                        {
                            label: 'Floodplain, Floodway, or Restriction Zone',
                            description: (
                                <p>
                                    The site cannot be in a flood plain, flood way, or restriction zone, unless the
                                    applicable general plan or zoning ordinance contains provisions to mitigate the risk
                                    of a flood.
                                </p>
                            ),
                            requirements: ['flood_plain_9_4']
                        },
                        {
                            label: 'Wildfire hazard',
                            description: (
                                <p>
                                    The site cannot be subject to a wildland fire hazard, as determined by the
                                    Department of Forestry and Fire Protection,{' '}
                                    <em>
                                        unless the applicable general plan or zoning ordinance contains provisions to
                                        mitigate the risk of a wildland fire hazard.
                                    </em>
                                </p>
                            ),
                            requirements: ['wildfire_hazard_9_3']
                        }
                    ],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: (
                                <p>
                                    Residential or Mixed-Use (25% can be "primarily neighborhood-serving goods,
                                    services, or retail uses")
                                </p>
                            )
                        },
                        {
                            label: 'Infill Site',
                            description: (
                                <>
                                    <p>Must be on an infill site as defined by PRC § 21061.3.</p>
                                    <p>
                                        "Infill site" means a site in an urbanized area that meets either of the
                                        following criteria:
                                    </p>
                                    <ol type="a">
                                        <li>
                                            The site has not been previously developed for urban uses and both of the
                                            following apply:
                                            <ol type="1">
                                                <li>
                                                    The site is immediately adjacent to parcels that are developed with
                                                    qualified urban uses, or at least 75 percent of the perimeter of the
                                                    site adjoins parcels that are developed with qualified urban uses,
                                                    and the remaining 25 percent of the site adjoins parcels that have
                                                    previously been developed for qualified urban uses.
                                                </li>
                                                <li>
                                                    No parcel within the site has been created within the past 10 years{' '}
                                                    <em>
                                                        unless the parcel was created as a result of the plan of a
                                                        redevelopment agency.
                                                    </em>
                                                </li>
                                            </ol>
                                        </li>
                                        <li>The site has been previously developed for qualified urban uses.</li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Acreage Limitation',
                            description: <p>Not more than 4 acres in total area.</p>
                        },
                        {
                            label: 'Number of Units',
                            description: <p>100 residential units or less</p>
                        },
                        {
                            label: 'Size Limitations',
                            description: (
                                <p>
                                    The project does not include any single level building that exceeds 100,000 square
                                    feet.
                                </p>
                            )
                        },
                        {
                            label: 'Density',
                            description: (
                                <p>
                                    The project promotes higher density infill housing. A project with a density of at
                                    least 20 units per acre shall be conclusively presumed to promote higher density
                                    infill housing. A project with a density of at least 10 units per acre and a density
                                    greater than the average density of the residential properties within 1,500 feet
                                    shall be presumed to promote higher density housing{' '}
                                    <em>unless the preponderance of the evidence demonstrates otherwise.</em>
                                </p>
                            )
                        },
                        {
                            label: 'Community-Level Environmental Review',
                            description: (
                                <>
                                    <p>
                                        Within five years of the date that the application for the project is deemed
                                        complete pursuant to GOV § 65943, community-level environmental review was
                                        certified or adopted.
                                    </p>
                                    <p>"Community-level environmental review" means either of the following:</p>
                                    <ol type="1">
                                        <li>
                                            An environmental impact report certified on any of the following:
                                            <ol type="A">
                                                <li>A general plan.</li>
                                                <li>
                                                    A revision or update to the general plan that includes at least the
                                                    land use and circulation elements.
                                                </li>
                                                <li>An applicable community plan.</li>
                                                <li>An applicable specific plan.</li>
                                                <li>
                                                    A housing element of the general plan, if the environmental impact
                                                    report analyzed the environmental effects of the density of the
                                                    proposed project.
                                                </li>
                                            </ol>
                                        </li>
                                        <li>
                                            Pursuant to this division and the implementing guidelines adopted pursuant
                                            to this division that govern subsequent review following a program
                                            environmental impact report, or pursuant to PRC § 21157.1, 21157.5, or
                                            21166, a negative declaration or mitigated negative declaration was adopted
                                            as a subsequent environmental review document, following and based upon an
                                            environmental impact report on any of the projects listed in subparagraphs
                                            (A), (C), or (D) of paragraph (1).
                                        </li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Plan Consistency',
                            description: (
                                <p>
                                    The project is consistent with any applicable general plan, specific plan, and local
                                    coastal program, including any mitigation measures required by a plan or program, as
                                    that plan or program existed on the date that the application was deemed complete
                                    and with any applicable zoning ordinance, as that zoning ordinance existed on the
                                    date that the application was deemed complete, except that a project shall not be
                                    deemed to be inconsistent with the zoning designation for the site if that zoning
                                    designation is inconsistent with the general plan only because the project site has
                                    not been rezoned to conform with a more recently adopted general plan.
                                </p>
                            )
                        },
                        {
                            label: 'Affordable Housing',
                            description: (
                                <>
                                    <p>Either of the following criteria are met:</p>
                                    <ol type="A">
                                        <li>
                                            <ol type="i">
                                                <li>
                                                    At least 10 percent of the housing is sold to families of moderate
                                                    income, or not less than 10 percent of the housing is rented to
                                                    families of low income, or not less than 5 percent of the housing is
                                                    rented to families of very low income.
                                                </li>
                                                <li>
                                                    The project developer provides sufficient legal commitments to the
                                                    appropriate local agency to ensure the continued availability and
                                                    use of the housing units for very low, low-, and moderate-income
                                                    households at monthly housing costs determined pursuant to{' '}
                                                    <a
                                                        href="https://1.next.westlaw.com/Link/Document/FullText?findType=L&originatingContext=document&transitionType=DocumentItem&pubNum=1000211&refType=SP&originatingDoc=Ie4039d901a0d11e9a89d8c1249eb3f1e&cite=CAGTS65589.5"
                                                        target="_blank"
                                                    >
                                                        paragraph (3) of subdivision (h) of GOV § 65589.5.
                                                    </a>
                                                    .
                                                </li>
                                            </ol>
                                        </li>
                                        <li>
                                            The project developer has paid or will pay in-lieu fees pursuant to a local
                                            ordinance in an amount sufficient to result in the development of an
                                            equivalent number of units that would otherwise be required pursuant to
                                            subparagraph (A).
                                        </li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Open Space',
                            description: (
                                <>
                                    <p>The project site is not located on developed open space.</p>
                                    <ol type="A">
                                        <li>
                                            "Developed open space" means land that meets all of the following criteria:
                                            <ol type="i">
                                                <li>
                                                    Is publicly owned, or financed in whole or in part by public funds.
                                                </li>
                                                <li>Is generally open to, and available for use by, the public.</li>
                                                <li>
                                                    Is predominantly lacking in structural development other than
                                                    structures associated with open spaces, including, but not limited
                                                    to, playgrounds, swimming pools, ballfields, enclosed child play
                                                    areas, and picnic facilities.
                                                </li>
                                            </ol>
                                        </li>
                                        <li>
                                            For the purposes of this paragraph, "developed open space" includes land
                                            that has been designated for acquisition by a public agency for developed
                                            open space, but does not include lands acquired with public funds dedicated
                                            to the acquisition of land for housing purposes.
                                        </li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Utilities',
                            description: (
                                <p>
                                    The project and other projects approved prior to the approval of the project can be
                                    adequately served by existing utilities, and the project applicant has paid, or has
                                    committed to pay, all applicable in-lieu or development fees.
                                </p>
                            )
                        },
                        {
                            label: 'Species',
                            description: (
                                <p>
                                    The project does not harm any species protected by the federal Endangered Species
                                    Act of 1973 (16 U.S.C. §1531 et seq.), the Native Plant Protection Act (Chapter 10
                                    (commencing with § 1900) of Division 2 of the Fish and Game Code), or the California
                                    Endangered Species Act (Chapter 1.5 (commencing with § 2050) of Division 3 of the
                                    Fish and Game Code), and the project does not cause the destruction or removal of
                                    any species protected by a local ordinance in effect at the time the application for
                                    the project was deemed complete.
                                </p>
                            )
                        },
                        {
                            label: 'Site Contamination',
                            description: (
                                <p>
                                    The site is not included on any list of facilities and sites compiled pursuant to
                                    GOV § 65962.5.
                                </p>
                            )
                        },
                        {
                            label: 'High Risk of Fire or Explosion',
                            description: (
                                <p>
                                    The site is not subject to an unusually high risk of fire or explosion from
                                    materials stored or used on nearby properties.
                                </p>
                            )
                        },
                        {
                            label: 'Hazards',
                            description: (
                                <>
                                    <p>
                                        The site is subject to a preliminary endangerment assessment prepared by an
                                        environmental assessor to determine the existence of any release of a hazardous
                                        substance on the site and to determine the potential for exposure of future
                                        occupants to significant health hazards from any nearby property or activity.
                                    </p>
                                    <p>
                                        If a release of a hazardous substance is found to exist on the site, the release
                                        shall be removed or any significant effects of the release shall be mitigated to
                                        a level of insignificance in compliance with state and federal requirements.
                                    </p>
                                    <p>
                                        If a potential for exposure to significant hazards from surrounding properties
                                        or activities is found to exist, the effects of the potential exposure shall be
                                        mitigated to a level of insignificance in compliance with state and federal
                                        requirements.
                                    </p>
                                </>
                            )
                        },
                        {
                            label: 'Historical Resources',
                            description: (
                                <p>
                                    The project does not have a significant effect on historical resources pursuant to
                                    PRC § 21084.1.
                                </p>
                            )
                        },
                        {
                            label: 'Public Health',
                            description: (
                                <p>
                                    The site is not subject to risk of a public health exposure at a level that would
                                    exceed the standards established by any state or federal agency.
                                </p>
                            )
                        },
                        {
                            label: 'Exceptions',
                            description: (
                                <>
                                    <p>Cannot use this exemption if any of the following occur:</p>
                                    <ol type="1">
                                        <li>
                                            There is a reasonable possibility that the project will have a
                                            project-specific, significant effect on the environment due to unusual
                                            circumstances.
                                        </li>
                                        <li>
                                            Substantial changes with respect to the circumstances under which the
                                            project is being undertaken that are related to the project have occurred
                                            since community-level environmental review was certified or adopted.
                                        </li>
                                        <li>
                                            New information becomes available regarding the circumstances under which
                                            the project is being undertaken and that is related to the project, that was
                                            not known, and could not have been known, at the time that community-level
                                            environmental review was certified or adopted.
                                        </li>
                                    </ol>
                                    <p>
                                        However, if all other requirements are met, the analysis of the environmental
                                        effects of the project in the environmental impact report or the negative
                                        declaration shall be limited to an analysis of the project-specific effect of
                                        the projects and any effects identified pursuant to paragraph (2) or (3).
                                    </p>
                                </>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_21159_25',
                label: 'PRC § 21159.25 (AB 1804, Unincorporated Infill)',
                notes: (
                    <p>
                        This exemption is similar to the Class 32 Categorical Exemption (§ 15332) but for unincorporated
                        areas. While a Notice of Exemption (NOE) is usually optional, an NOE must be filed if this
                        particular exemption is used (see PRC § 21159.25(d)). This exemption is only in effect until
                        January 1, 2025.
                    </p>
                ),
                requirements: {
                    spatial: ['unincorporated_2_4', 'urbanized_area_or_urban_cluster'],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Residential or Mixed-Use (66% residential); must be multifamily.</p>
                        },
                        {
                            label: 'Infill',
                            description: (
                                <>
                                    <p>The site is substantially surrounded by qualified urban uses.</p>
                                    <p>
                                        "Substantially surrounded" means at least 75 percent of the perimeter of the
                                        project site adjoins, or is separated only by an improved public right-of-way
                                        from, parcels that are developed with qualified urban uses. The remainder of the
                                        perimeter of the site adjoins, or is separated only by an improved public
                                        right-of-way from, parcels that have been designated for qualified urban uses in
                                        a zoning, community plan, or general plan for which an environmental impact
                                        report was certified.
                                    </p>
                                    <p>Qualified Urban Uses as defined in PRC § 21072:</p>
                                    <p>
                                        "Qualified urban use" means any residential, commercial, public institutional,
                                        transit or transportation passenger facility, or retail use, or any combination
                                        of those uses.
                                    </p>
                                </>
                            )
                        },
                        {
                            label: 'Planning Consistency',
                            description: (
                                <p>
                                    The project is consistent with the applicable general plan designation and all
                                    applicable general plan policies as well as with applicable zoning designation and
                                    regulations.
                                </p>
                            )
                        },
                        {
                            label: 'Density',
                            description: (
                                <>
                                    <p>
                                        The density of the residential portion of the project is not less than the
                                        greater of the following:
                                    </p>
                                    <ol type="i">
                                        <li>
                                            The average density of the residential properties that adjoin, or are
                                            separated only by an improved public right-of-way from, the perimeter of the
                                            project site, if any.
                                        </li>
                                        <li>
                                            The average density of the residential properties within 1,500 feet of the
                                            project site.
                                        </li>
                                        <li>Six dwelling units per acre.</li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'Number of Units',
                            description: <p>Six or more residential units</p>
                        },
                        {
                            label: 'Acreage',
                            description: <p>No more than five acres</p>
                        },
                        {
                            label: 'Utilities',
                            description: (
                                <p>The site can be adequately served by all required utilities and public services.</p>
                            )
                        },
                        {
                            label: 'Habitat',
                            description: (
                                <p>The site has no value as habitat for endangered, rare, or threatened species.</p>
                            )
                        },
                        {
                            label: 'Significant Impacts',
                            description: (
                                <p>
                                    Approval of the project would not result in any significant effects relating to
                                    transportation, noise, air quality, greenhouse gas emissions, or water quality.
                                </p>
                            )
                        },
                        {
                            label: 'Exceptions',
                            description: <p>The exemption does not apply if any of the following conditions exist:</p>,
                            show: true,
                            requirements: [
                                {
                                    label: 'Cumulative Impact',
                                    description: (
                                        <p>
                                            The cumulative impact of successive projects of the same type in the same
                                            place, over time is significant.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Significant Effect',
                                    description: (
                                        <p>
                                            There is a reasonable possibility that the project will have a significant
                                            effect on the environment due to unusual circumstances.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Scenic Highways',
                                    description: (
                                        <p>
                                            The project may result in damage to scenic resources, including, but not
                                            limited to, trees, historic buildings, rock outcroppings, or similar
                                            resources, within a highway officially designated as a state scenic highway.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Hazardous Waste Sites',
                                    description: (
                                        <p>
                                            The project is located on a site which is included on any list compiled
                                            pursuant to Section 65962.5 of the Government Code.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Historical Resources',
                                    description: (
                                        <p>
                                            The project may cause a substantial adverse change in the significance of a
                                            historical resource.
                                        </p>
                                    )
                                }
                            ]
                        }
                    ]
                }
            },
            {
                code: 'e_65457',
                label: 'Govt. Code § 65457 (Specific Plan)',
                notes: (
                    <p>
                        This exemption covers residential development projects, including a subdivision or zoning
                        change. A lawsuit to challenge use of this exemption must be filed within 30 days of the lead
                        agency's decision to carry out or approve the project (see GOV § 65457(b)). This exemption is
                        also contained in CEQA Guidelines § 15182.
                    </p>
                ),
                requirements: {
                    spatial: [
                        {
                            label: 'Specific Plan',
                            description: (
                                <>
                                    <p>
                                        Covered by a specific plan for which an environmental impact report has been
                                        certified after January 1, 1980.
                                    </p>
                                    <p>
                                        <em>
                                            Site Check's data only considers whether the parcel may be covered by a
                                            specific plan. Please confirm with the local agency that the specific plan
                                            was analyzed with an environmental impact report certified after January 1,
                                            1980. Note, some areas may be covered by specific plans that have not been
                                            inputted into this tool, which is why this requirement may have a ? symbol.
                                        </em>
                                    </p>
                                </>
                            ),
                            strict: false,
                            requirements: ['covered_by_a_specific_plan_2_6']
                        }
                    ],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Residential Only</p>
                        },
                        {
                            label: 'Plan Consistency',
                            description: <p>Must be consistent with the specific plan</p>
                        },
                        {
                            label: 'Exceptions',
                            description: (
                                <p>
                                    Cannot use if an event specified in PRC § 21166 has occurred{' '}
                                    <em>
                                        unless and until a supplemental environmental impact report for the specific
                                        plan is prepared and certified.
                                    </em>
                                </p>
                            )
                        }
                    ]
                }
            }
        ]
    },
    {
        category: 'categorical',
        label: 'Categorical Exemptions',
        description: (
            <p>
                Categorical exemptions are made up of classes of projects that generally are considered not to have
                potential impacts on the environment. Categorical exemptions are identified by the Natural Resources
                Agency and are defined in the CEQA Guidelines (§§ 15300-15333). All categorical exemptions are subject
                to the limitations contained in CEQA Guidelines § 15300.2.{' '}
                <em>
                    Only the two categorical exemptions regularly used for housing are mapped and reported; other
                    categorical exemptions could potentially be utilized and should be reviewed and considered.
                </em>
            </p>
        ),
        exemptions: [
            {
                code: 'e_15303',
                label: 'CEQA Guidelines § 15303 (Class 3, Small Structures)',
                notes: (
                    <p>
                        This categorical exemption covers the construction or conversion of small structures. Housing
                        projects are listed as an example of the types of projects that are covered.
                    </p>
                ),
                requirements: reqs => {
                    const isUrbanizedArea = !!reqs.urbanized_area_prc_21071_2_1

                    return {
                        spatial: [],
                        project: [
                            {
                                label: 'Type of Housing',
                                description: (
                                    <>
                                        <p>Residential--single family and multifamily</p>
                                        {isUrbanizedArea ? (
                                            <>
                                                <p>
                                                    Because the project is in an urbanized area, this exemption covers
                                                    the construction or conversion of up to three single-family
                                                    residences. It also covers apartments, duplexes and similar
                                                    structures designed for not more than six dwelling units.
                                                </p>
                                                <p>
                                                    <em>
                                                        Note: See the report for PRC § 21159.24 to determine if this
                                                        parcel is in an unincorporated urbanized area, where additional
                                                        actions are needed before this parcel can qualify as urbanized.
                                                    </em>
                                                </p>
                                            </>
                                        ) : (
                                            <p>
                                                Because the project is not in an urbanized area, this exemption covers
                                                one single-family residence, or a second dwelling unit. It also covers a
                                                duplex or similar multi-family residential structure, totaling no more
                                                than four dwelling units.
                                            </p>
                                        )}
                                    </>
                                )
                            },
                            {
                                label: 'Zoning',
                                description: <p>Must be zoned residential</p>
                            },
                            {
                                label: 'Exceptions',
                                description: <p>Subject to the limitations contained in CEQA Guidelines § 15300.2:</p>,
                                show: true,
                                requirements: [
                                    {
                                        label: 'Location',
                                        description: (
                                            <p>
                                                Class 3 is qualified by consideration of where the project is to be
                                                located --a project that is ordinarily insignificant in its impact on
                                                the environment may in a particularly sensitive environment be
                                                significant. Therefore, Class 3 considered to apply in all instances,
                                                except where the project may impact on an environmental resource of
                                                hazardous or critical concern where designated, precisely mapped, and
                                                officially adopted pursuant to law by federal, state, or local agencies.
                                            </p>
                                        )
                                    },
                                    {
                                        label: 'Cumulative Impact',
                                        description: (
                                            <p>
                                                All exemptions for these classes are inapplicable when the cumulative
                                                impact of successive projects of the same type in the same place, over
                                                time is significant.
                                            </p>
                                        )
                                    },
                                    {
                                        label: 'Significant Effect',
                                        description: (
                                            <p>
                                                A categorical exemption shall not be used for an activity where there is
                                                a reasonable possibility that the activity will have a significant
                                                effect on the environment due to unusual circumstances.
                                            </p>
                                        )
                                    },
                                    {
                                        label: 'Scenic Highways',
                                        description: (
                                            <p>
                                                A categorical exemption shall not be used for a project which may result
                                                in damage to scenic resources, including but not limited to, trees,
                                                historic buildings, rock outcroppings, or similar resources, within a
                                                highway officially designated as a State Scenic Highway. This does not
                                                apply to improvements which are required as mitigation by an adopted
                                                negative declaration or certified EIR.
                                            </p>
                                        )
                                    },
                                    {
                                        label: 'Hazardous Waste Sites',
                                        description: (
                                            <p>
                                                A categorical exemption shall not be used for a project located on a
                                                site which is included on any list compiled pursuant to GOV §65962.5.
                                            </p>
                                        )
                                    },
                                    {
                                        label: 'Historical Resources',
                                        description: (
                                            <p>
                                                A categorical exemption shall not be used for a project which may cause
                                                a substantial adverse change in the significance of a historical
                                                resource.
                                            </p>
                                        )
                                    }
                                ]
                            }
                        ]
                    }
                }
            },
            {
                code: 'e_15332',
                label: 'CEQA Guidelines § 15332 (Class 32, Infill Development Projects)',
                notes: (
                    <p>
                        This categorical exemption covers all infill projects, including projects that contain
                        residential uses.
                    </p>
                ),
                requirements: {
                    spatial: ['within_city_limits_2_3'],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Any</p>
                        },
                        {
                            label: 'Plan Consistency',
                            description: (
                                <p>
                                    The project is consistent with the applicable general plan designation and all
                                    applicable general plan policies as well as with applicable zoning designation and
                                    regulations.
                                </p>
                            )
                        },
                        {
                            label: 'Acreage',
                            description: <p>The project site is no more than five acres.</p>
                        },
                        {
                            label: 'Infill',
                            description: <p>The project site is substantially surrounded by urban uses.</p>
                        },
                        {
                            label: 'Habitat',
                            description: (
                                <p>
                                    The project site has no value as habitat for endangered, rare or threatened species.
                                </p>
                            )
                        },
                        {
                            label: 'Significant Impacts',
                            description: (
                                <p>
                                    Approval of the project would not result in any significant effects relating to
                                    traffic, noise, air quality, or water quality.
                                </p>
                            )
                        },
                        {
                            label: 'Utilities',
                            description: (
                                <p>The site can be adequately served by all required utilities and public services.</p>
                            )
                        },
                        {
                            label: 'Exceptions',
                            description: <p>None of the exceptions contained in CEQA Guidelines § 15300.2 apply:</p>,
                            show: true,
                            requirements: [
                                {
                                    label: 'Cumulative Impact',
                                    description: (
                                        <p>
                                            All exemptions for these classes are inapplicable when the cumulative impact
                                            of successive projects of the same type in the same place, over time is
                                            significant.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Significant Effect',
                                    description: (
                                        <p>
                                            A categorical exemption shall not be used for an activity where there is a
                                            reasonable possibility that the activity will have a significant effect on
                                            the environment due to unusual circumstances.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Scenic Highways',
                                    description: (
                                        <p>
                                            A categorical exemption shall not be used for a project which may result in
                                            damage to scenic resources, including but not limited to, trees, historic
                                            buildings, rock outcroppings, or similar resources, within a highway
                                            officially designated as a State Scenic Highway. This does not apply to
                                            improvements which are required as mitigation by an adopted negative
                                            declaration or certified EIR.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Hazardous Waste Sites',
                                    description: (
                                        <p>
                                            A categorical exemption shall not be used for a project located on a site
                                            which is included on any list compiled pursuant to GOV § 65962.5.
                                        </p>
                                    )
                                },
                                {
                                    label: 'Historical Resources',
                                    description: (
                                        <p>
                                            A categorical exemption shall not be used for a project which may cause a
                                            substantial adverse change in the significance of a historical resource.
                                        </p>
                                    )
                                }
                            ]
                        }
                    ]
                }
            }
        ]
    },
    {
        category: 'streamlining',
        label: 'Streamlining',
        description: (
            <p>
                Streamlining provisions under CEQA range from a complete exemption to an obligation to prepare a
                narrowed environmental document with fewer topics.
            </p>
        ),
        exemptions: [
            {
                code: 'e_21094_5',
                label: 'PRC § 21094.5 (SB 226, Infill Streamlining)',
                notes: (
                    <p>
                        This provision provides a streamlined review process for infill projects that satisfy specified
                        performance standards by limiting the topics subject to review at the project level where the
                        effects of infill development have been addressed in a planning level decision or by uniformly
                        applicable development policies. Streamlining under this section will range from a complete
                        exemption to an obligation to prepare a narrowed, project-specific environmental document (see
                        CEQA Guidelines § 15183.3(c), (d)). A notice of determination must be filed if this code section
                        is used to obtain a full exemption from CEQA (see CEQA Guidelines § 15183.3(d)(2)(A)). For more
                        information on this provision, see CEQA Guidelines Appendix M (performance standards prepared
                        pursuant to PRC § 21094.5.5) and Appendix N (infill environmental checklist form).
                    </p>
                ),
                requirements: reqs => {
                    const withinMpo = !!reqs.within_mpo_2_5
                    return {
                        spatial: [
                            'urban_area_prc_21094_2_2',
                            {
                                label: 'Transit Proximity',
                                description: (
                                    <>
                                        <p>Any of the following:</p>
                                        <p>
                                            <em>
                                                Note, even if a project does not meet any of the transit proximity
                                                requirements listed below, it may still qualify for this streamlining
                                                provision if it is a residential or mixed-use project consisting of 300
                                                or fewer residential units, all of which are affordable to low income
                                                households. (See CEQA Guidelines, Appendix M, Section IV.A.)
                                            </em>
                                        </p>
                                    </>
                                ),
                                op: 'or',
                                show: true,
                                strict: false,
                                requirements: [
                                    {
                                        label: 'Existing Major Transit Stop',
                                        description: (
                                            <>
                                                <p>Within ½ mile of an existing Major Transit Stop.</p>
                                                <p>
                                                    "Major Transit Stop" means a site containing an existing rail
                                                    transit station, a ferry terminal served by either a bus or rail
                                                    transit service, or the intersection of two or more major bus routes
                                                    with frequencies of service intervals of 15 minutes or less during
                                                    the morning and afternoon peak commute periods.
                                                </p>
                                            </>
                                        ),
                                        requirements: ['within_half_mile_major_transit_']
                                    },
                                    {
                                        label: 'Planned Major Transit Stop',
                                        description: (
                                            <>
                                                <p>Within ½ mile of a planned Major Transit Stop.</p>
                                                <p>
                                                    A planned and funded stop that is included in an adopted regional
                                                    transportation improvement program.
                                                </p>
                                            </>
                                        ),
                                        requirements: ['planned_rtp_half_mile_major_tra']
                                    },
                                    {
                                        label: 'Existing High-Quality Transit Corridor',
                                        description: (
                                            <>
                                                <p>
                                                    Within ½ mile of a stop along an existing High-Quality Transit
                                                    Corridor.
                                                </p>
                                                <p>
                                                    "High-quality transit corridor" means an existing corridor with
                                                    fixed route bus service with service intervals no longer than 15
                                                    minutes during peak commute hours.
                                                </p>
                                            </>
                                        ),
                                        requirements: ['within_half_mile_transit_corrid']
                                    },
                                    {
                                        label: 'Planned High-Quality Transit Corridor',
                                        description: (
                                            <>
                                                <p>
                                                    Within ½ mile of a stop along a planned High-Quality Transit
                                                    Corridor.
                                                </p>
                                                <p>
                                                    A planned and funded stop that is included in an adopted regional
                                                    transportation improvement program.
                                                </p>
                                            </>
                                        ),
                                        requirements: ['planned_rtip_half_mile_stop_hqt']
                                    },
                                    {
                                        label: 'Low Vehicle Travel Area',
                                        description: (
                                            <>
                                                <p>Within a Low Vehicle Travel Area.</p>
                                                <p>
                                                    "Low vehicle travel area" means a traffic analysis zone that
                                                    exhibits a below average existing level of travel as determined
                                                    using a regional travel demand model. For residential projects,
                                                    travel refers to either home-based or household vehicle miles
                                                    traveled per capita. For commercial and retail projects, travel
                                                    refers to non-work attraction trip length; however, where such data
                                                    are not available, commercial projects reference either home-based
                                                    or household vehicle miles traveled per capita.
                                                </p>
                                            </>
                                        ),
                                        requirements: ['low_vehicle_travel_area_3_8']
                                    }
                                ]
                            }
                        ],
                        project: [
                            {
                                label: 'Type of Housing',
                                description: <p>Residential or Mixed-Use (undefined)</p>
                            },
                            {
                                label: 'Infill',
                                description: (
                                    <>
                                        <p>
                                            Site either has been previously developed or a vacant site that adjoins
                                            existing qualified urban uses on at least seventy-five percent of the site's
                                            perimeter.
                                        </p>
                                        <p>
                                            "Adjoins" includes uses that are separated only by an improved public
                                            right-of-way.
                                        </p>
                                    </>
                                )
                            },
                            withinMpo
                                ? {
                                      label: 'SCS Consistency',
                                      description: (
                                          <p>
                                              Since this is within the boundaries of an MPO, the project must be
                                              consistent with the general use designation, density, building intensity,
                                              and applicable policies specified for the project area in the applicable
                                              Sustainable Communities Strategy.
                                          </p>
                                      )
                                  }
                                : {
                                      label: '"Small Walkable Community Project"',
                                      description: (
                                          <>
                                              <p>
                                                  Since this is outside the boundaries of an MPO, the project must be a
                                                  "small walkable community project."
                                              </p>
                                              <p>
                                                  "Small walkable community project" is defined as a project that is in
                                                  an incorporated city and that satisfies the following requirements:
                                              </p>
                                              <ol type="A">
                                                  <li>
                                                      Has a project area of approximately one-quarter mile diameter of
                                                      contiguous land completely within the existing incorporated
                                                      boundaries of the city.
                                                  </li>
                                                  <li>
                                                      Has a project area that includes a residential area adjacent to a
                                                      retail downtown area.
                                                  </li>
                                                  <li>
                                                      The project has a density of at least eight dwelling units per
                                                      acre or a floor area ratio for retail or commercial use of not
                                                      less than 0.50.
                                                  </li>
                                              </ol>
                                          </>
                                      )
                                  },
                            {
                                label: 'Soil and Water Remediation',
                                description: (
                                    <p>
                                        If the project site is included on any list compiled pursuant to GOV § 65962.5,
                                        the project shall document how it has remediated the site, if remediation is
                                        completed. Alternatively, the project shall implement the recommendations
                                        provided in a preliminary endangerment assessment or comparable document that
                                        identifies remediation appropriate for the site.
                                    </p>
                                )
                            },
                            {
                                label: 'Residential Units Near High-Volume Roadways and Stationary Sources',
                                description: (
                                    <p>
                                        If a project includes residential units located within 500 feet, or other
                                        distance determined to be appropriate by the local agency or air district based
                                        on local conditions, of a high volume roadway or other significant sources of
                                        air pollution, the project shall comply with any policies and standards
                                        identified in the local general plan, specific plan, zoning code or community
                                        risk reduction plan for the protection of public health from such sources of air
                                        pollution. If the local government has not adopted such plans or policies, the
                                        project shall include measures, such as enhanced air filtration and project
                                        design, that the lead agency finds, based on substantial evidence, will promote
                                        the protection of public health from sources of air pollution. Those measure may
                                        include, among others, the recommendations of the California Air Resources
                                        Board, air districts, and the California Air Pollution Control Officers
                                        Association.
                                    </p>
                                )
                            }
                        ]
                    }
                }
            },
            {
                code: 'e_21099',
                label: 'PRC § 21099 (SB 743, Aesthetics & Parking)',
                notes: (
                    <p>
                        This provision removes the requirement to analyze aesthetic and parking impacts. It does not
                        affect, change, or modify the authority of a lead agency to consider aesthetic impacts pursuant
                        to local design review ordinances or other discretionary powers provided by other laws or
                        policies. For the purposes of this streamlining provision, aesthetic impacts do not include
                        impacts on historical or cultural resources.
                    </p>
                ),
                requirements: {
                    spatial: ['transit_priority_area_3_3'],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Residential or mixed-use residential (not defined)</p>
                        },
                        {
                            label: 'Infill',
                            description: (
                                <>
                                    <p>Must be located on an infill site as defined in PRC § 21099:</p>
                                    <p>
                                        "Infill site" means a lot located within an urban area that has been previously
                                        developed, or on a vacant site where at least 75 percent of the perimeter of the
                                        site adjoins, or is separated only by an improved public right-of-way from,
                                        parcels that are developed with qualified urban uses.
                                    </p>
                                </>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_21155_2',
                label: 'PRC § 21155.2 (SB 375, SCEA)',
                notes: (
                    <p>
                        This provision applies to transit priority projects, which are defined in PRC § 21155. If this
                        criterion is met, then the project might qualify for a Sustainable Communities Environmental
                        Assessment (SCEA), which is essentially a negative declaration that is subject to a substantial
                        evidence standard of review (see PRC § 21155.2(b)). If the project does not qualify for an SCEA,
                        it may qualify for a streamlined environmental impact report (see PRC § 21155.2(c)).
                    </p>
                ),
                requirements: {
                    spatial: [
                        {
                            label: 'Transit Proximity',
                            description: (
                                <>
                                    <p>Any of the following:</p>
                                    <p>
                                        <em>
                                            Note, a project is considered to be within one-half mile of a major transit
                                            stop or high-quality transit corridor if all parcels within the project have
                                            no more than 25 percent of their area farther than one-half mile from the
                                            stop or corridor and if not more than 10 percent of the residential units or
                                            100 units, whichever is less, in the project are farther than one-half mile
                                            from the stop or corridor. (See PRC § 21155.)
                                        </em>
                                    </p>
                                </>
                            ),
                            op: 'or',
                            show: true,
                            requirements: [
                                {
                                    label: 'Existing Major Transit Stop',
                                    description: (
                                        <>
                                            <p>
                                                Within ½ mile of an existing Major Transit Stop as defined in PRC §
                                                21064.3:
                                            </p>
                                            <p>"Major transit stop" means a site containing any of the following:</p>
                                            <ol type="a">
                                                <li>An existing rail or bus rapid transit station.</li>
                                                <li>
                                                    A ferry terminal served by either a bus or rail transit service.
                                                </li>
                                                <li>
                                                    The intersection of two or more major bus routes with a frequency of
                                                    service interval of 15 minutes or less during the morning and
                                                    afternoon peak commute periods.
                                                </li>
                                                <li>"Bus rapid transit" is defined in PRC § 21060.2.</li>
                                            </ol>
                                        </>
                                    ),
                                    requirements: ['within_half_mile_major_transit_']
                                },
                                {
                                    label: 'Planned Major Transit Stop',
                                    description: (
                                        <p>
                                            Within ½ mile of a planned Major Transit Stop as defined in PRC § 21064.3
                                            that is included in the applicable Regional Transportation Plan.
                                        </p>
                                    ),
                                    requirements: ['planned_rtp_half_mile_major_tra']
                                },
                                {
                                    label: 'Existing High-Quality Transit Corridor',
                                    description: (
                                        <>
                                            <p>Within ½ mile of an existing High-Quality Transit Corridor:</p>
                                            <p>
                                                A high-quality transit corridor means a corridor with fixed route bus
                                                service with service intervals no longer than 15 minutes during peak
                                                commute hours.
                                            </p>
                                        </>
                                    ),
                                    requirements: ['within_half_mile_transit_corrid']
                                },
                                {
                                    label: 'Planned High-Quality Transit Corridor',
                                    description: (
                                        <p>
                                            Within ½ mile of a planned High-Quality Transit Corridor that is included in
                                            the applicable Regional Transportation Plan.
                                        </p>
                                    ),
                                    requirements: ['planned_rtp_half_mile_hqtc_3_12']
                                }
                            ]
                        },
                        'within_mpo_2_5'
                    ],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: (
                                <p>
                                    Residential and mixed use (at least 50 percent residential use based on total
                                    building square footage)
                                </p>
                            )
                        },
                        {
                            label: 'Density',
                            description: (
                                <p>
                                    Provide a minimum net density of at least 20 dwelling units per acre; if the project
                                    contains between 26 percent and 50 percent nonresidential uses, a floor area ratio
                                    of not less than 0.75.
                                </p>
                            )
                        },
                        {
                            label: 'SCS consistency',
                            description: (
                                <p>
                                    Consistent with the general use designation, density, building intensity, and
                                    applicable policies specified for the project area in the applicable Sustainable
                                    Communities Strategy.
                                </p>
                            )
                        },
                        {
                            label: 'Prior EIR',
                            description: (
                                <p>
                                    Must have incorporated all feasible mitigation measures, performance standards, or
                                    criteria set forth in the prior applicable environmental impact reports and adopted
                                    in findings made pursuant to PRC § 21081.
                                </p>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_21159_28',
                label: 'PRC § 21159.28 (SB 375, Growth Inducing & Cumulative Impacts)',
                notes: (
                    <p>
                        Under this provision, an environmental document does not need to reference, describe, or discuss
                        (1) growth inducing impacts; or (2) any project specific or cumulative impacts from cars and
                        light-duty truck trips generated by the project on global warming or the regional transportation
                        network (see PRC § 21159.28(a)). Any environmental impact report is not required to reference,
                        describe, or discuss a reduced residential density alternative to address the effects of car and
                        light-duty truck trips generated by the project (see PRC § 21159.28(b)).
                    </p>
                ),
                requirements: {
                    spatial: ['within_mpo_2_5'],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: (
                                <>
                                    <p>Residential or Mixed-Use</p>
                                    <p>
                                        A residential or mixed-use residential project is a project where at least 75
                                        percent of the total building square footage of the project consists of
                                        residential use.
                                    </p>
                                    <p>Or a transit priority project (see if PRC § 21155.2 applies).</p>
                                </>
                            )
                        },
                        {
                            label: 'SCS Consistency',
                            description: (
                                <p>
                                    Consistent with the use designation, density, building intensity, and applicable
                                    policies specified for the project area in the applicable sustainable communities
                                    strategy.
                                </p>
                            )
                        },
                        {
                            label: 'Prior EIR',
                            description: (
                                <p>
                                    Project incorporates the mitigation measures required by an applicable prior
                                    environmental document.
                                </p>
                            )
                        }
                    ]
                }
            },
            {
                code: 'e_15183',
                label: 'CEQA Guidelines § 15183 (Planning and Zoning Consistency) ',
                notes: (
                    <p>
                        While not a mappable provision at this time, users should also consider whether CEQA Guidelines
                        § 15183 applies. Projects that are consistent with the development density established by
                        existing zoning, community plan, or general plan policies for which an EIR was certified may not
                        require additional environmental review, except as might be necessary to examine whether there
                        are project-specific significant effects which are peculiar to the project or its site.
                    </p>
                ),
                requirements: {
                    spatial: [],
                    project: [
                        {
                            label: 'Type of Housing',
                            description: <p>Any</p>
                        },
                        {
                            label: 'Plan or Zoning Consistency',
                            description: (
                                <>
                                    <p>The project is consistent with:</p>
                                    <ol type="1">
                                        <li>A community plan adopted as part of a general plan</li>
                                        <li>
                                            A zoning action which zoned or designated the parcel on which the project
                                            would be located to accommodate a particular density of development, OR
                                        </li>
                                        <li>A general plan of a local agency</li>
                                    </ol>
                                </>
                            )
                        },
                        {
                            label: 'EIR',
                            description: (
                                <p>
                                    An environmental impact report was certified by the lead agency for the zoning
                                    action, the community plan, or the general plan.
                                </p>
                            )
                        }
                    ]
                }
            }
        ]
    }
]
